import { Button, FileButton, Group, Text } from '@mantine/core';
import { IconCamera } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

/**
 * Xodim fotosi (yuz tanish terminali — Hikvision uchun) — o'qituvchi va xodim
 * kartochkalarida YAGONA blok. `POST/DELETE /staff/:id/photo` bilan ishlaydi;
 * backend ruxsati MANAGERS, bu sahifalar esa faqat DIRECTOR/ADMIN'ga ochiq
 * (`lib/permissions.ts`) — shu sabab bu yerda alohida rol tekshiruvi yo'q.
 *
 * MUHIM: `GET /staff/:id` hozircha `avatar` maydonini QAYTARMAYDI. Shuning uchun
 * muvaffaqiyatdan keyin query invalidate QILINMAYDI — refetch javobida `avatar`
 * bo'lmagani uchun endigina yozilgan qiymatni cache'dan yuvib yuborardi. O'rniga
 * POST javobidagi kalit `onChanged` orqali sahifa cache'iga qo'lda yoziladi
 * (sahifa qayta ochilganda foto backend GET'ga `avatar` qo'shilgunga qadar
 * ko'rinmay turadi — bu backend tomonda hal qilinishi kerak bo'lgan bo'shliq).
 */
export function StaffPhotoControl({ userId, avatar, onChanged }: {
  userId: string;
  /** `User.avatar` IKKI xil qiymat saqlaydi: S3 kaliti YOKI `sticker:<...>` */
  avatar?: string | null;
  /** Yangi qiymatni sahifa cache'iga yozish (yuqoridagi izohga qarang) */
  onChanged: (avatar: string | null) => void;
}) {
  const { t } = useTranslation();
  const err = (e: unknown) =>
    notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error') });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file); // backend multer maydon nomi — "file"
      const { data } = await api.post<{ avatar: string; url: string }>(`/staff/${userId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (data) => { notifications.show({ color: 'green', message: t('staffPhoto.uploaded') }); onChanged(data.avatar); },
    onError: err,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/staff/${userId}/photo`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('staffPhoto.removed') }); onChanged(null); },
    onError: err,
  });

  // Stiker — Premium foydalanuvchining o'z tanlovi, yuz tanish uchun yaroqsiz:
  // "foto bor" deb hisoblanmaydi va o'chirish tugmasi ham ko'rsatilmaydi
  // (backend DELETE stikerni ham bo'shatadi, lekin admin uni bexosdan
  // o'chirib qo'ymasligi uchun UI'da faqat haqiqiy foto o'chiriladi).
  const isSticker = !!avatar && avatar.startsWith('sticker:');
  const hasPhoto = !!avatar && !isSticker;

  return (
    <Group gap={4} mt={4} wrap="wrap">
      <FileButton onChange={(f) => f && upload.mutate(f)} accept="image/png,image/jpeg,image/webp">
        {(props) => (
          <Button {...props} size="compact-xs" variant="light" loading={upload.isPending} leftSection={<IconCamera size={14} />}>
            {hasPhoto ? t('staffPhoto.change') : t('staffPhoto.upload')}
          </Button>
        )}
      </FileButton>
      {hasPhoto && (
        <Button size="compact-xs" variant="subtle" color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
          {t('staffPhoto.remove')}
        </Button>
      )}
      {isSticker && <Text size="xs" c="dimmed">{t('staffPhoto.stickerNote')}</Text>}
    </Group>
  );
}
